import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";

import PrivateRoute from "./components/PrivateRoute";
import NotFound from "./pages/auth/NotFound";

/* ================= COMMON ================= */
import Login from "./pages/auth/Login";
import ForgotPasswordEmail from "./pages/auth/ForgotPasswordEmail";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import LocationAttendanceReport from "./pages/shared/LocationAttendanceReport";
import PrivacyPolicy from "./pages/shared/PrivacyPolicy";


/* SHARED */
import LeaveReport from "./pages/shared/LeaveReport";
import AttendanceReport from "./pages/shared/AttendanceReport";

/* ================= ADMIN ================= */
import AdminProfile from "./pages/admin/AdminProfile";
import AdminCreateEmployee from "./pages/shared/CreateEmployee";
import AdminEmployees from "./pages/shared/UserList";
import AdminProjects from "./pages/shared/ProjectList";
import AdminUserDetails from "./pages/shared/UserDetail";
import AdminProjectProgress from "./pages/shared/ProjectProgress";
import UpdateUser from "./pages/admin/UpdateUser";
import UpdateLocations from "./pages/admin/UpdateLocations";
import AddLeavePolpcy from "./pages/admin/AddLeavePolicy";
import ManageCalendar from "./pages/admin/ManageCalendar";
import AdminAllowOverRide from "./pages/shared/AllowOverride";
import AdminPendingLeaveList from "./pages/shared/PendingLeaveList";
import AdminMarkAttendance from "./pages/shared/MarkAttendance";
import AdminRequestLeave from "./pages/shared/RequestLeave";
import SendMail from "./pages/admin/SendMail";
import MailTracking from "./pages/admin/MailTracking";

/* ================= MANAGER ================= */
import ManagerProfile from "./pages/manager/ManagerProfile";
import ManagerActivity from "./pages/manager/ManagerActivity";
import ManagerProgress from "./pages/manager/ManagerProgress";
import ManagerCreateProject from "./pages/manager/CreateProject";
import ManagerCreateEmployee from "./pages/shared/CreateEmployee";
import ManagerProjects from "./pages/shared/ProjectList";
import ManagerEmployees from "./pages/shared/UserList";
import ManagerUserDetails from "./pages/shared/UserDetail";
import ManagerProjectProgress from "./pages/shared/ProjectProgress";
import ProjectDetails from "./pages/shared/ProjectDetails";
import CreateDiscipline from "./pages/manager/CreateDiscipline";
import ManagerAllowOverRide from "./pages/shared/AllowOverride";
import ManagerPendingLeaveList from "./pages/shared/PendingLeaveList";
import ManagerMarkAttendance from "./pages/shared/MarkAttendance";
import ManagerRequestLeave from "./pages/shared/RequestLeave";
import BulkCreateProjects from "./pages/manager/BulkCreateProjects";
import BulkCreateDisciplines from "./pages/manager/BulkCreateDisciplines";
import TeamReview from "./pages/shared/TeamReview";
import AddStages from "./pages/manager/AddStages";
import BulkCreateStages from "./pages/manager/BulkCreateStages";


/* ================= EMPLOYEE ================= */
import EmployeeProfile from "./pages/employee/EmployeeProfile";
import EmployeeTimeSheet from "./pages/employee/EmployeeTimeSheet";
import EmployeeMarkAttendance from "./pages/shared/MarkAttendance";
import EmployeeRequestLeave from "./pages/shared/RequestLeave";
import EmployeeProjectProgress from "./pages/employee/EmployeeProjectProgress";
import EmployeeActivity from "./pages/employee/EmployeeActivity";
import EmployeeActivityProgress from "./pages/employee/EmployeeActivityProgress";

/* ================= CHAT ================= */
import ChatLayout from './pages/Chat/ChatLayout';
import NotificationHandler from "./components/NotificationHandler";
import useAuthStore from "./store/authStore";
import useChatStore from "./store/chatStore";

function RoleRedirect({ path }) {
    const role = useAuthStore(state => state.role || "EMPLOYEE").toLowerCase();
    return <Navigate to={`/${role}/${path}`} replace />;
}

function RootRedirect() {
    const token = useAuthStore(state => state.token);
    const role = useAuthStore(state => state.role || "EMPLOYEE").toLowerCase();
    const search = window.location.search;

    if (token) {
        const params = new URLSearchParams(search);
        const redirect = params.get('redirect');
        if (redirect) {
            let target = `/${role}/${redirect}`;
            if (redirect === 'chat') {
                const convId = params.get('conversation_id');
                if (convId) {
                    try {
                        useChatStore.getState().selectConversation(Number(convId));
                    } catch (e) {
                        console.warn("Failed to select conversation in RootRedirect:", e);
                    }
                }
                target = `/${role}/chat`;
            } else if (redirect === 'pending-leave-list') {
                target = `/${role}/pending-leave-list`;
            } else if (redirect === 'leave-report') {
                target = `/${role}/leave-report`;
            } else if (redirect === 'dashboard') {
                target = `/${role}/dashboard`;
            }
            return <Navigate to={target} replace />;
        }
        return <Navigate to={`/${role}/dashboard`} replace />;
    }
    return <Navigate to={`/login${search}`} replace />;
}

function App() {
    return (
        <Router>
            <NotificationHandler />
            <Routes>
                {/* ROLE-BASED REDIRECTS FOR DEEP LINKS */}
                <Route path="/chat" element={<RoleRedirect path="chat" />} />
                <Route path="/pending-leave-list" element={<RoleRedirect path="pending-leave-list" />} />
                <Route path="/leave-report" element={<RoleRedirect path="leave-report" />} />
                <Route path="/dashboard" element={<RoleRedirect path="dashboard" />} />

                {/* AUTH */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPasswordEmail />} />
                {/* For reset link and manual reset */}
                <Route path="/reset-password/:uid/:token" element={<ForgotPassword />} />
                {/* Logged-in users changing password */}
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* PUBLIC LEGAL & POLICY PAGES */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* ================= SHARED ROUTES ================= */}
                <Route element={<PrivateRoute allowedRoles={["ADMIN", "MANAGER", "EMPLOYEE"]} />}>
                    <Route path="/employee/assigned-projects" element={<EmployeeTimeSheet />} />
                </Route>

                {/* ================= ADMIN ================= */}
                <Route element={<PrivateRoute allowedRoles={["ADMIN"]} />}>
                    <Route path="/admin/dashboard" element={<AdminProfile />} />
                    <Route path="/admin/create-user" element={<AdminCreateEmployee />} />
                    <Route path="/admin/users" element={<AdminEmployees />} />
                    <Route path="/admin/projects" element={<AdminProjects />} />
                    <Route path="/admin/projects/:projectId/view" element={<ProjectDetails role="ADMIN" readOnly={true} />} />
                    <Route path="/admin/user-details/:employeeId" element={<AdminUserDetails />} />
                    <Route path="/admin/projects/:projectId/progress" element={<AdminProjectProgress />} />
                    <Route path="/admin/update-user/:employeeId" element={<UpdateUser />} />
                    <Route path="/admin/update-locations" element={<UpdateLocations />} />
                    <Route path="/admin/add-leave-policy" element={<AddLeavePolpcy />} />
                    <Route path="/admin/manage-calendar" element={<ManageCalendar />} />
                    <Route path="/admin/allow-over-ride" element={<AdminAllowOverRide />} />
                    <Route path="/admin/pending-leave-list" element={<AdminPendingLeaveList />} />
                    <Route path="/admin/mark-attendance" element={<AdminMarkAttendance />} />
                    <Route path="/admin/attendance-report/:employeeId?" element={<AttendanceReport />} />
                    <Route path="/admin/location-attendance-report" element={<LocationAttendanceReport />} />
                    <Route path="/admin/leave-report/:employeeId?" element={<LeaveReport />} />
                    <Route path="/admin/request-leave" element={<AdminRequestLeave />} />
                    <Route path="/admin/chat" element={<ChatLayout />} />
                    <Route path="/admin/team-review" element={<TeamReview />} />
                    <Route path="/admin/send-mail" element={<SendMail />} />
                    <Route path="/admin/mail-tracking" element={<MailTracking />} />
                </Route>

                {/* ================= SHARED ADMIN & MANAGER ================= */}
                <Route element={<PrivateRoute allowedRoles={["ADMIN", "MANAGER"]} />}>
                    <Route path="/manager/activity" element={<ManagerActivity />} />
                    <Route path="/manager/activity/progress" element={<ManagerProgress />} />
                </Route>

                <Route element={<PrivateRoute allowedRoles={["MANAGER"]} />}>
                    <Route path="/manager/dashboard" element={<ManagerProfile />} />
                    <Route path="/manager/create-project" element={<ManagerCreateProject />} />
                    <Route path="/manager/create-user" element={<ManagerCreateEmployee />} />
                    <Route path="/manager/user-details/:employeeId" element={<ManagerUserDetails />} />
                    <Route path="/manager/projects/:projectId/progress" element={<ManagerProjectProgress />} />
                    <Route path="/manager/projects" element={<ManagerProjects />} />
                    <Route path="/manager/users" element={<ManagerEmployees />} />
                    <Route path="/manager/discipline-details/:projectId" element={<ProjectDetails />} />
                    <Route path="/manager/projects/:projectId/create-discipline" element={<CreateDiscipline />} />
                    <Route path="/manager/projects/:projectId/disciplines/:disciplineId/stages" element={<AddStages />} />
                    <Route path="/manager/allow-over-ride" element={<ManagerAllowOverRide />} />
                    <Route path="/manager/pending-leave-list" element={<ManagerPendingLeaveList />} />
                    <Route path="/manager/mark-attendance" element={<ManagerMarkAttendance />} />
                    <Route path="/manager/attendance-report/:employeeId?" element={<AttendanceReport />} />
                    <Route path="/manager/location-attendance-report" element={<LocationAttendanceReport />} />
                    <Route path="/manager/leave-report/:employeeId?" element={<LeaveReport />} />
                    <Route path="/manager/request-leave" element={<ManagerRequestLeave />} />
                    <Route path="/manager/projects/bulk-create" element={<BulkCreateProjects />} />
                    <Route path="/manager/projects/:projectId/disciplines/bulk-create" element={<BulkCreateDisciplines />} />
                    <Route path="/manager/projects/:projectId/disciplines/:disciplineId/stages/bulk" element={<BulkCreateStages />} />
                    <Route path="/manager/chat" element={<ChatLayout />} />
                    <Route path="/manager/team-review" element={<TeamReview />} />
                </Route>

                {/* ================= EMPLOYEE ================= */}
                <Route element={<PrivateRoute allowedRoles={["EMPLOYEE"]} />}>
                    <Route path="/employee/dashboard" element={<EmployeeProfile />} />

                    <Route path="/employee/mark-attendance" element={<EmployeeMarkAttendance />} />
                    <Route path="/employee/attendance-report" element={<AttendanceReport />} />
                    <Route path="/employee/leave-report" element={<LeaveReport />} />
                    <Route path="/employee/request-leave" element={<EmployeeRequestLeave />} />
                    <Route path="/employee/progress" element={<EmployeeProjectProgress />} />
                    <Route path="/employee/activity" element={<EmployeeActivity />} />
                    <Route path="/employee/activity/progress" element={<EmployeeActivityProgress />} />
                    <Route path="/employee/chat" element={<ChatLayout />} />
                </Route>

                {/* FALLBACK */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Router >
    );
}

export default App;